"use client";

import { useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, FlagTriangleRight, Landmark, Utensils, Waves } from "lucide-react";
import { Link } from "@/components/common/Link";
const AUTO_ROTATE_MS = 6000;
const CITY_STORIES = [{
  id: "golf-courses",
  title: "Da Nang Golf Courses",
  description: "World-class courses with stunning coastal and mountain views.",
  icon: FlagTriangleRight,
  /*
   * Replace imageSrc with the final uploaded WordPress image.
   * The fallback keeps the section working now.
   */
  imageSrc: "",
  fallbackSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1674648/img/da_nang_panoramic.png",
  imagePosition: "center center",
  link: "/blog/da-nang-golf-travel-guide/#golf-courses",
  iconBackground: "#078A72"
}, {
  id: "culture-heritage",
  title: "Culture & Heritage",
  description: "Ancient towns, temples and local traditions.",
  icon: Landmark,
  /*
   * Replace imageSrc with the final uploaded WordPress image.
   */
  imageSrc: "",
  fallbackSrc: "https://ideal-jay.10web.cloud/wp-content/uploads/2026/07/Vietnam.png",
  imagePosition: "center center",
  link: "/blog/da-nang-golf-travel-guide/#culture-heritage",
  iconBackground: "#B79A5F"
}, {
  id: "cuisine-local-food",
  title: "Cuisine & Local Food",
  description: "Fresh seafood, vibrant markets and authentic local flavours.",
  icon: Utensils,
  /*
   * Replace imageSrc with your uploaded cuisine image URL.
   * A working fallback is used until then.
   */
  imageSrc: "",
  fallbackSrc: "https://ideal-jay.10web.cloud/wp-content/uploads/2026/07/Vietnam.png",
  imagePosition: "center center",
  link: "/blog/da-nang-golf-travel-guide/#cuisine-local-food",
  iconBackground: "#B8512F"
}, {
  id: "beaches-nature",
  title: "Beaches & Nature",
  description: "Beautiful coastlines, lush mountains and outdoor escapes.",
  icon: Waves,
  /*
   * Replace imageSrc with your uploaded beach image URL.
   */
  imageSrc: "",
  fallbackSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1674648/img/da_nang_panoramic.png",
  imagePosition: "center center",
  link: "/blog/da-nang-golf-travel-guide/#beaches-nature",
  iconBackground: "#467F88"
}];
export default function HomeCityDiscovery() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const showPreviousStory = () => {
    setActiveIndex(current => current === 0 ? CITY_STORIES.length - 1 : current - 1);
  };
  const showNextStory = () => {
    setActiveIndex(current => (current + 1) % CITY_STORIES.length);
  };
  useEffect(() => {
    if (isPaused) {
      return;
    }
    const interval = window.setInterval(() => {
      setActiveIndex(current => (current + 1) % CITY_STORIES.length);
    }, AUTO_ROTATE_MS);
    return () => {
      window.clearInterval(interval);
    };
  }, [isPaused]);
  return <section id="home-city-discovery" data-home-section="city-discovery" aria-label="Discover Da Nang" className="relative w-full overflow-hidden bg-[#FFFDF7] px-5 pb-20 pt-12 !h-[980px] md:px-8 md:pb-24 md:pt-16 lg:px-12 lg:pb-28" style={{
    fontFamily: '"Raleway", Arial, sans-serif'
  }}>
      <div className="mx-auto w-full max-w-[1480px]" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
        {/* Main Da Nang feature */}
        <div className={["relative w-full overflow-hidden", "min-h-[610px]", "rounded-[18px]", "border border-[#DED3BF]", "bg-[#073F31]", "shadow-[0_18px_46px_rgba(7,63,49,0.13)]", "md:min-h-[570px]", "lg:min-h-[520px]"].join(" ")}>
          {/* Rotating background images */}
          {CITY_STORIES.map((story, index) => {
          const resolvedImage = story.imageSrc || story.fallbackSrc;
          return <img key={story.id} src={resolvedImage} alt={story.title} aria-hidden={index !== activeIndex} style={{
            objectPosition: story.imagePosition
          }} className={["absolute inset-0 h-full w-full", "object-cover", "transition-[opacity,transform] duration-700 ease-out", index === activeIndex ? "scale-100 opacity-100" : "pointer-events-none scale-[1.025] opacity-0"].join(" ")} />;
        })}

          {/* Ivory-to-transparent editorial gradient */}
          <div aria-hidden="true" className={["pointer-events-none absolute inset-0 z-[1]", "bg-[linear-gradient(90deg,rgba(255,253,247,0.98)_0%,rgba(255,253,247,0.94)_27%,rgba(255,253,247,0.72)_43%,rgba(255,253,247,0.16)_68%,rgba(255,253,247,0)_100%)]"].join(" ")} />

          {/* Soft lower shading behind overlapping cards */}
          <div aria-hidden="true" className={["pointer-events-none absolute inset-x-0 bottom-0 z-[1]", "h-[44%]", "bg-[linear-gradient(180deg,rgba(3,39,31,0)_0%,rgba(3,39,31,0.18)_100%)]"].join(" ")} />

          {/* Editorial content */}
          <div className={["relative z-10", "max-w-[650px]", "px-7 pb-[210px] pt-12", "sm:px-10", "md:px-14 md:pb-[220px] md:pt-14", "lg:px-16 lg:pb-[190px] lg:pt-16"].join(" ")}>
            <p className={["text-[11px] font-bold uppercase", "tracking-[0.2em]", "text-[#073F31]"].join(" ")}>
              City Discovery
            </p>

            <div className="mt-3 h-px w-[84px] bg-[#B79A5F]" />

            <h2 className={["mt-4 max-w-[620px]", "text-[42px] font-medium", "leading-[1.02]", "tracking-[-0.035em]", "text-[#073F31]", "sm:text-[48px]", "md:text-[54px]", "lg:text-[60px]"].join(" ")} style={{
            fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif'
          }}>
              Discover a different
              <br className="hidden sm:block" /> side of{" "}
              <span className="text-[#93C900]"><span className="!font-semibold"><span className="!font-sans"><span className="!font-default">Da Nang</span></span></span>.</span>
            </h2>

            {/* Small gold editorial detail */}
            <div aria-hidden="true" className="mt-5 flex items-center gap-[3px]">
              {[0, 1, 2, 3].map(item => <span key={item} className={["block h-[10px] w-[18px]", "rounded-b-full", "border-b-2 border-[#B79A5F]"].join(" ")} />)}
            </div>

            <p className={["mt-6 max-w-[500px]", "text-[15px] font-medium", "leading-[1.7]", "text-[#29483E]", "sm:text-[16px]"].join(" ")}>
              Great golf, epic beaches, heritage, cuisine and culture — all in
              one unforgettable coastal city.
            </p>

            <Link to="/blog/da-nang-golf-travel-guide/" className={["group mt-6 inline-flex", "h-[52px] min-w-[210px]", "items-center justify-between", "gap-5", "rounded-[8px]", "bg-[#073F31]", "px-5", "text-[11px] font-bold uppercase", "tracking-[0.075em]", "leading-none", "text-[#BFFF00]", "no-underline", "shadow-[0_10px_26px_rgba(7,63,49,0.18)]", "transition-all duration-300", "hover:-translate-y-[2px]", "hover:bg-[#03271F]"].join(" ")}>
              <span>Explore Da Nang</span>

              <ArrowRight className="size-[17px] transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} aria-hidden="true" />
            </Link>
          </div>

          {/* Previous and next controls */}
          <div className={["absolute right-5 top-5 z-20", "flex items-center gap-2", "md:right-7 md:top-7"].join(" ")}>
            <button type="button" onClick={showPreviousStory} aria-label="Show previous Da Nang story" className={["flex size-[42px]", "items-center justify-center", "rounded-full", "border border-[#D8C69B]/75", "bg-[#FFFDF7]/86", "text-[#073F31]", "shadow-[0_8px_22px_rgba(7,63,49,0.12)]", "backdrop-blur-md", "transition-all duration-200", "hover:border-[#BFFF00]", "hover:bg-[#073F31]", "hover:text-[#BFFF00]"].join(" ")}>
              <ChevronLeft className="size-[18px]" strokeWidth={1.8} aria-hidden="true" />
            </button>

            <button type="button" onClick={showNextStory} aria-label="Show next Da Nang story" className={["flex size-[42px]", "items-center justify-center", "rounded-full", "border border-[#D8C69B]/75", "bg-[#FFFDF7]/86", "text-[#073F31]", "shadow-[0_8px_22px_rgba(7,63,49,0.12)]", "backdrop-blur-md", "transition-all duration-200", "hover:border-[#BFFF00]", "hover:bg-[#073F31]", "hover:text-[#BFFF00]"].join(" ")}>
              <ChevronRight className="size-[18px]" strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Four story cards overlapping the feature */}
        <div className={["relative z-20", "-mt-[150px]", "px-4", "sm:px-7", "md:-mt-[160px] md:px-10", "lg:-mt-[145px] lg:px-12"].join(" ")}>
          <div className={["flex snap-x snap-mandatory", "gap-4 overflow-x-auto", "pb-4", "sm:grid sm:grid-cols-2", "sm:overflow-visible", "sm:gap-5", "lg:grid-cols-4", "lg:gap-5", "[scrollbar-width:none]", "[&::-webkit-scrollbar]:hidden"].join(" ")}>
            {CITY_STORIES.map((story, index) => {
            const Icon = story.icon;
            const resolvedImage = story.imageSrc || story.fallbackSrc;
            const isActive = index === activeIndex;
            return <Link key={story.id} to={story.link} aria-label={`Explore ${story.title}`} aria-current={isActive ? "true" : undefined} onMouseEnter={() => setActiveIndex(index)} onFocus={() => setActiveIndex(index)} className={["group relative block", "h-[320px]", "min-w-[82%]", "snap-center overflow-hidden", "rounded-[13px]", "border", "bg-[#073F31]", "no-underline", "shadow-[0_14px_32px_rgba(7,63,49,0.17)]", "transition-all duration-300", "hover:-translate-y-[5px]", "hover:shadow-[0_20px_42px_rgba(7,63,49,0.22)]", "sm:min-w-0", "md:h-[330px]", "lg:h-[340px]", isActive ? "border-[#BFFF00] ring-1 ring-[#BFFF00]/65" : "border-[#D8C69B]/50"].join(" ")}>
                  <img src={resolvedImage} alt="" aria-hidden="true" loading="lazy" style={{
                objectPosition: story.imagePosition
              }} className={["absolute inset-0 h-full w-full", "object-cover", "transition-transform duration-700", "group-hover:scale-[1.045]"].join(" ")} />

                  <div aria-hidden="true" className={["absolute inset-0", "bg-[linear-gradient(180deg,rgba(3,39,31,0.02)_16%,rgba(3,39,31,0.12)_40%,rgba(3,39,31,0.72)_69%,rgba(3,39,31,0.98)_100%)]"].join(" ")} />

                  <div className={["absolute inset-x-0 bottom-0 z-10", "px-5 pb-5 pt-20", "md:px-6 md:pb-6"].join(" ")}>
                    <span className={["mb-4 flex size-[46px]", "items-center justify-center", "rounded-full", "border border-white/20", "text-white", "shadow-[0_7px_18px_rgba(0,0,0,0.20)]"].join(" ")} style={{
                  backgroundColor: story.iconBackground
                }}>
                      <Icon className="size-[21px]" strokeWidth={1.55} aria-hidden="true" />
                    </span>

                    <h3 className={["max-w-[240px]", "text-[25px] font-medium", "leading-[1.05]", "tracking-[-0.025em]", "text-[#FFFDF7]", "lg:text-[27px]"].join(" ")} style={{
                  fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif'
                }}>
                      {story.title}
                    </h3>

                    <div className="mt-3 h-px w-8 bg-[#D8BD67]" />

                    <p className={["mt-3 max-w-[250px]", "text-[13px] font-medium", "leading-[1.5]", "text-[#FFFDF7]/84"].join(" ")}>
                      {story.description}
                    </p>

                    <span className={["mt-4 inline-flex items-center gap-2", "text-[10px] font-bold uppercase", "tracking-[0.15em]", "text-[#BFFF00]", "transition-all duration-300", "group-hover:gap-3"].join(" ")}>
                      Explore

                      <ArrowRight className="size-[14px]" strokeWidth={1.8} aria-hidden="true" />
                    </span>
                  </div>
                </Link>;
          })}
          </div>

          {/* Carousel indicators */}
          <div className="mt-4 flex items-center justify-center gap-2">
            {CITY_STORIES.map((story, index) => <button key={story.id} type="button" onClick={() => setActiveIndex(index)} aria-label={`Show ${story.title}`} aria-current={index === activeIndex ? "true" : undefined} className={["h-[7px] rounded-full border-0 p-0", "transition-all duration-300", index === activeIndex ? "w-7 bg-[#93C900]" : "w-[7px] bg-[#B7C0BB] hover:bg-[#073F31]"].join(" ")} />)}
          </div>
        </div>
      </div>
    </section>;
}